import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiError } from '../../../lib/api/_request.js';
import type { SpecFeatureRow, SpecFeatures } from '../../../lib/api/spec-requirements.js';

/** 列と見出しの翻訳キー。module scope では hook を呼べないので鍵で持つ。 @graph-connects none */
const COLUMNS = [
  ['area', 'spec.features.columns.area'],
  ['requirement', 'spec.features.columns.requirement'],
  ['asis', 'spec.features.columns.asis'],
  ['tobe', 'spec.features.columns.tobe'],
  ['note', 'spec.features.columns.note'],
] as const satisfies readonly (readonly [keyof SpecFeatureRow, string])[];

/** 空の 1 行。 @graph-connects none */
const EMPTY_ROW: SpecFeatureRow = { area: '', requirement: '', asis: '', tobe: '', note: '' };

export interface FeaturesEditorProps {
  /** 今保存されている機能一覧。無い案件では null */
  features: SpecFeatures | null;
  /** 保存中かどうか */
  saving: boolean;
  /** 保存が失敗したときの原因。列ごとの上限・未決・ASIS=TOBE はここに API のメッセージが入る */
  error: unknown;
  /** 保存する */
  onSave: (rows: SpecFeatureRow[]) => void;
  /** 編集をやめる */
  onCancel: () => void;
}

/**
 * 保存できなかった理由を人が読む形にする。API が返したメッセージはそのまま見せる
 * （入口で訳し直すと、API 側で文言が変わったときに黙ってずれる）。
 * @graph-connects none
 */
function reason(t: TFunction, error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) return t('spec.error.forbidden');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return t('spec.error.saveFailed');
}

/**
 * 機能一覧を編集する。
 *
 * 列ごとの文字数上限・行数上限・備考の未決・ASIS と TOBE が同じ、の判定は API が持つ
 * （`check_features` と同じ規則）。画面は**今の文字数だけ**を出し、超過は保存時に返る
 * メッセージをそのまま見せる。上限の数を画面に持たせると、変わったときに黙ってずれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 機能一覧（領域・要件・ASIS・TOBE・備考）を表として編集して保存する
 * @graph-connects ApiError [references] 保存が失敗した理由の判別
 */
export function FeaturesEditor({ features, saving, error, onSave, onCancel }: FeaturesEditorProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SpecFeatureRow[]>(() =>
    features === null || features.rows.length === 0 ? [{ ...EMPTY_ROW }] : features.rows,
  );

  const setCell = (index: number, key: keyof SpecFeatureRow, value: string) => {
    setRows((current) => current.map((r, i) => (i === index ? { ...r, [key]: value } : r)));
  };

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        // 全部空の行は送らない（API は行数で数えるので、空行が上限を食う）
        onSave(rows.filter((r) => COLUMNS.some(([key]) => r[key].trim() !== '')));
      }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="w-12">
                <span className="sr-only">{t('spec.section.rowNumber')}</span>
              </th>
              {COLUMNS.map(([key, labelKey]) => (
                <th
                  key={key}
                  className="text-left text-xs font-semibold text-[var(--color-text-secondary)] pb-1"
                >
                  {t(labelKey)}
                </th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              // 並べ替えではなく入れ替えなので index を鍵にしてよい
              <tr key={index}>
                <td className="pr-2 pb-2 align-top text-xs font-mono whitespace-nowrap text-[var(--color-text-secondary)]">
                  {row.no === undefined ? '' : `F-${String(row.no)}`}
                </td>
                {COLUMNS.map(([key, labelKey]) => (
                  <td key={key} className="pr-2 pb-2 align-top">
                    <textarea
                      className="block w-full border border-[var(--color-border)] rounded px-2 py-1 text-sm"
                      rows={2}
                      value={row[key]}
                      aria-label={t('spec.section.rowFieldAria', {
                        row: index + 1,
                        field: t(labelKey),
                      })}
                      onChange={(e) => {
                        setCell(index, key, e.target.value);
                      }}
                    />
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {t('spec.charCount', { n: row[key].length })}
                    </span>
                  </td>
                ))}
                <td className="align-top pb-2">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
                    aria-label={t('spec.section.rowRemoveAria', { row: index + 1 })}
                    onClick={() => {
                      setRows((current) => current.filter((_, i) => i !== index));
                    }}
                  >
                    {t('spec.action.remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="mt-1 text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
        onClick={() => {
          setRows((current) => [...current, { ...EMPTY_ROW }]);
        }}
      >
        {t('spec.action.addRow')}
      </button>

      {error === null || error === undefined ? null : (
        <p role="alert" className="mt-3 text-sm text-[var(--color-error)] whitespace-pre-wrap">
          {reason(t, error)}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="text-sm px-4 py-2 rounded bg-[var(--color-accent-primary)] text-white disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        >
          {t(saving ? 'spec.action.saving' : 'spec.action.save')}
        </button>
        <button
          type="button"
          className="text-sm px-4 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] cursor-pointer disabled:cursor-not-allowed"
          onClick={onCancel}
        >
          {t('spec.action.cancel')}
        </button>
      </div>
    </form>
  );
}
